<template>
  <div :key="recipientSender" class="gm-recipient-settings">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>

    <div id="groovemail-server-table-view">
      <server-table
        class="text-center groovemail-recipient-table"
        url="recipient"
        ref="serverTable"
        tableRef="recipientsTable"
        tableTitle="frontend_groovemail_settings_recipients_settings"
        :slotData="recipientSlotData"
        :tableColumns="recipientsSettingTableColumns"
        :tableOptions="recipientsSettingTableOptions"
        addNewBtnText="frontend_groovemail_settings_recipients_add_new_recipient"
        :emptyStateData="emptyStateData"
        @addNewData="showRecipientModal"
        @loaded="hideLoader()"
        @loading="showLoader()"
      >
      </server-table>
    </div>

    <gd-modal
      id="add-recipient-modal"
      name="add-recipient-modal"
      :title="
        modalType === 'add'
          ? $t('frontend_groovemail_settings_recipients_add_new_recipient')
          : $t('frontend_groovemail_settings_recipients_edit_recipient')
      "
      @closed="addRecipientModalClosed()"
    >
      <AddNewRecipient
        ref="addNewRecipientModal"
        :model="model"
        :modalType="modalType"
        :validator="$v.model"
        :recipientId="recipientId"
        :urls="urls"
        @showLoader="showLoader()"
        @hideLoader="hideLoader()"
        @refreshRecipientsTable="refreshRecipientsTable"
        @showErrorMessage="requestErrorAlert"
        @showSuccessMessage="showSuccessPopUp"
        @closeRecipientModal="closeRecipientModal"
      />
    </gd-modal>

    <!-- Confirmation delete modal -->
    <confirm-modal
      :ref="`confirmation-delete-recipients`"
      :name="`confirmation-delete-recipients`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_bouses_delete_bonus_text')"
      @confirm="removeRecipient"
      @close="closeDeleteRecipientsModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import {
required,
minLength,
maxLength,
  email,
} from 'vuelidate/lib/validators';
import ServerTable from '@groovepages/gd-ui-kit/components/tables/ServerTable.vue';
import AddNewRecipient from '@/components/AddNewRecipient.vue';
import monthsMixin from '@/mixin/monthsMixin.js';
import {
  showErrorDialog,
  showSuccessDialog,
} from '@/components/showDialog.js';
import { sendersUrl } from '@/utils/urls.js';
import { getData, postData, deleteData } from '@/utils/apiHelper.js';

import themeConfig from '@/theme/themeConfig.js';
import showAlert from '@/components/sweet-alert-popup.js';

let self;

export default {
  name: 'Recipients',
  props: ['userCanMail'],

  mixins: [monthsMixin],

  data() {
    return {
      modalType: 'add',
      recipientSender: 0,
      recipientId: null,
      isLoading: false,
      urls: {},
      model: {
        name: '',
        email: '',
      },
      otpFieldActive: false,
      recipientsTableKey: 3,
      recipientsSettingTableColumns: [
        'name',
        'email',
        'verified',
        'updated_at',
        'actions',
      ],
      emptyStateData: {
        emptyStateHeading: 'frontend_groovemail_settings_recipients_settings',
        emptyStateSubHeading: 'frontend_groovemail_recipients_settings_have_no_data',
        emptyStateBtnText: 'frontend_groovemail_settings_recipients_add_new_recipient',
      },
    };
  },

  components: {
    AddNewRecipient,
    ServerTable,
  },

  validations: {
    model: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
      email: {
        required,
        email,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
    },
  },

  watch: {
    // Used to refresh black list table when demo mode changes
    demoMode() {
      this.refreshRecipientsTable();
    },
  },

  computed: {
    // Used to return demoMode value from store.
    demoMode() {
      return this.$store.state.demoMode.isDemoMode;
    },

    /**
     * used to return table options for recipients table.
     * @returns {object} table options for recipients table.
    */
    recipientsSettingTableOptions() {
      return {
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        pagination: { chunk: 5 },
        rowClassCallback: () => 'hover:bg-grey-50 funnelRow',
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: ['name', 'email', 'updated_at'],
        filterable: ['name', 'email'],
        headings: {
          name: () => this.$t('frontend_name'),
          email: () => this.$t('frontend_email'),
          verified: () => this.$t('frontend_groovemail_lead_listing_advanced_filters_status_verified'),
          updated_at: () => this.$t('frontend_groovemail_created_at'),
          actions: () => this.$t('frontend_groovemail_settings_integeration_listing_actions'),
        },
        requestFunction(data) {
          const requestParams = { ...data, ascending: 1 - data.ascending };
          const self = this;
          return axios
            .get(self.url, {
              params: requestParams,
            })
            .catch((e) => {
              self.dispatch('error', e);
            });
        },
        responseAdapter({ data }) {
          if (self.demoMode) {
            const newData = data?.data.map((obj) => ({
              ...obj,
              name: self.basicText,
              email: self.basicEmailText,
            }));

            return {
              data: newData,
              count: newData.length,
            };
          }
          return {
            data: data.data,
            count: data.total,
          };
        },
      };
    },

    /**
     * used to return slot data for recipients table.
     * @returns {Array} slot data for recipients table.
    */
    recipientSlotData() {
      return [
        {
          slotName: 'name',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.name ? props.row.name : this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'verified',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.is_verified === 1 ? 'Yes' : 'No',
        },
        {
          slotName: 'updated_at',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.updated_at ? 
            `${new Date(props.row.updated_at).getDate()} ${this.months[new Date(props.row.updated_at).getMonth()]}, ${new Date(props.row.updated_at).getFullYear()}` :
            this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          conditions: () => true,
          classes: () => {
            return {
              outerDiv: '',
              dropdown: '',
              outerSpan: '',
            };
          },
          actionList: [
            {
              id: 0,
              name: () => this.$t('frontend_edit'),
              conditions: () => true,
              callback: (props) => this.openEditRecipient(props.row.id),
            },
            {
              id: 1,
              name: () =>
                this.$t('frontend_groovemail_sender_listing_reverification'),
              conditions: (row) => this.userCanMail !== 0 && row.is_verified !== 1,
              callback: (props) => this.resendVerificationMail(props.row.id),
            },
            {
              id: 2,
              name: () => this.$t('frontend_delete'),
              conditions: () => true,
              callback: (props) => this.openDeleteRecipientsModal(props.row.id),
            },
          ],
        },
      ];
    },
  },

  created() {
    self = this;
    this.urls = {
      recipient: `${sendersUrl.recipient}`,
    };
  },

  methods: {

    // show loader
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    // hide loader
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    // error message for the request field
    async requestErrorAlert(error) {
      // hide the loader
      this.hideLoader();
      
      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark mb-3">${error || this.$t('frontend_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    // Used to refresh recipient table
    async refreshRecipientsTable() {
      // refresh recipient table
      await this.$refs.serverTable?.$refs?.recipientsTable?.refresh();
      
      // refresh recipientSender to refresh the recipient table
      this.recipientSender += 1;
    },

    // Opens a modal to add new recipients if user can mail else displays error message.
    showRecipientModal() {
      this.modalType = 'add';
      if (this.userCanMail) {
        this.$root.$emit('modal-open', 'add-recipient-modal');
      } else {
        this.$emit('showError');
      }
    },

    // Closes add recipient modal & resets data properties used.
    closeRecipientModal() {
      this.$root.$emit('modal-close', 'add-recipient-modal');
      this.addRecipientModalClosed();
    },

    // Resets add recipient modal data properties.
    addRecipientModalClosed() {
      this.model.name = '';
      this.model.email = '';
      this.$v.model.name.$reset();
      this.$v.model.email.$reset();
    },

    // open delete Recipients modal
    async openDeleteRecipientsModal(id) {
      // set the Recipients id
      this.recipientId = id;

      // open the modal
      this.$refs[`confirmation-delete-recipients`].open();
    },

    // close delete Recipients modal
    closeDeleteRecipientsModal() {
      // reset the Recipients id
      this.recipientId = null;

      // close the modal
      this.$refs[`confirmation-delete-recipients`].close();
    },

    // Used to delete recipient.
    async removeRecipient() {
      try {
        await deleteData(`${sendersUrl.recipient}/${this.recipientId}`);
        this.refreshRecipientsTable();

        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error alert
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    /**
     * Used to open a modal to edit recipient data.
     * @param {string} id contains unique id of recipient.
     */
    async openEditRecipient(id) {
      // show loader
      this.showLoader();

      try {
        this.modalType = 'edit';
        this.recipientId = id;
        const response = await getData(`${sendersUrl.recipient}/${id}`);
        if (response) {
          this.model.name = response?.data?.data?.name;
          this.model.email = response?.data?.data?.email;
          this.model.id = response?.data?.data?.id;
          this.originalEmail = this.model.email;
        }
        this.$root.$emit('modal-open', 'add-recipient-modal');

        // hide loader
        this.hideLoader();
      } catch (error) {
        // show error alert
        this.requestErrorAlert(error.response?.data?.error);
      }
    },

    /**
     * Used to resend verification mail.
     * @param {string} id contains unique id of email sender.
     */
    async resendVerificationMail(id) {
      try {
        const resp = await postData(
          `${sendersUrl.recipientEmailVerification}/${id}`
        );
        if (resp?.status === 200 && resp?.data?.message) {
          Notify.success(resp?.data?.message);
        }
      } catch (error) {
        // show error alert
        this.requestErrorAlert(
          error.response.state === 403 ? this.$t('frontend_groovemail_senders_resend_verification_mail_after_10_min_message')
          : error.response?.data?.error
        );
      }
    },

    /**
     * Displays error dialog.
     * @param {string} error error to be displayed.
     */
    async showAlertPopUp(error) {
      await showErrorDialog('', error);
    },

    /**
     * Displays a success dialog.
     *  @param {string} title  title for success message that needs to be displayed.
     *  @param {string} subTitle success message that needs to be displayed.
     */
    async showSuccessPopUp(title, subTitle) {
      await showSuccessDialog(title, subTitle);
    },
  },
};
</script>

<style lang="scss">
.groovemail-recipient-table {
  .VueTables__table {
    overflow: auto !important;
  }
  .VueTables__search-field {
    margin-bottom: 85px !important;
  }
  .VueTables__limit {
    bottom: -20px !important;
  }
  .VueTables--server table {
    margin-bottom: 50px;
  }
}
</style>

<style scoped>
.gm-recipient-settings,
.gm-recipient-settings ::v-deep #groovemail-server-table-view,
.gm-recipient-settings ::v-deep #gd-server-table-view,
.gm-recipient-settings ::v-deep .VueTables--server {
  min-width: 0;
  max-width: 100%;
}

/* ServerTable keeps its title, controls, and rows in separate legacy layers.
 * Scroll only the real responsive table viewport, so its existing search and
 * Add controls remain independently reachable on narrow screens. */
.gm-recipient-settings ::v-deep .table-responsive {
  clear: both;
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gm-recipient-settings ::v-deep .table-responsive:focus-within {
  outline: 2px solid var(--gs-primary);
  outline-offset: 2px;
}

.gm-recipient-settings ::v-deep .VueTables__table {
  min-width: 760px;
  table-layout: fixed;
}

.gm-recipient-settings ::v-deep .VueTables__table th,
.gm-recipient-settings ::v-deep .VueTables__table td {
  vertical-align: middle;
  border-color: var(--gs-border);
}

.gm-recipient-settings ::v-deep .VueTables__table th:nth-child(1),
.gm-recipient-settings ::v-deep .VueTables__table td:nth-child(1) {
  width: 23%;
  min-width: 170px;
}

.gm-recipient-settings ::v-deep .VueTables__table th:nth-child(2),
.gm-recipient-settings ::v-deep .VueTables__table td:nth-child(2) {
  width: 30%;
  min-width: 220px;
}

.gm-recipient-settings ::v-deep .VueTables__table th:nth-child(3),
.gm-recipient-settings ::v-deep .VueTables__table td:nth-child(3) {
  width: 12%;
  min-width: 96px;
}

.gm-recipient-settings ::v-deep .VueTables__table th:nth-child(4),
.gm-recipient-settings ::v-deep .VueTables__table td:nth-child(4) {
  width: 17%;
  min-width: 132px;
}

.gm-recipient-settings ::v-deep .VueTables__table th:nth-child(5),
.gm-recipient-settings ::v-deep .VueTables__table td:nth-child(5) {
  width: 18%;
  min-width: 142px;
  white-space: nowrap;
}

.gm-recipient-settings ::v-deep .VueTables__table td:nth-child(1),
.gm-recipient-settings ::v-deep .VueTables__table td:nth-child(2) {
  overflow-wrap: anywhere;
  word-break: normal;
}

.gm-recipient-settings ::v-deep .VueTables__table th:last-child span {
  display: inline;
}

.gm-recipient-settings ::v-deep .VueTables__table td:last-child .float-right {
  float: none;
}

.gm-recipient-settings ::v-deep .VueTables__table td:last-child .text-grey-500.h-10 {
  min-height: 2.75rem;
  min-width: 8.5rem;
}

.gm-recipient-settings ::v-deep .VueTables__table td:last-child .float-right [role="button"] > span {
  color: var(--gs-foreground);
}

.gm-recipient-settings ::v-deep .VueTables__table tbody tr:last-child td:last-child .relative.float-right > .absolute.pin-r {
  top: auto;
  bottom: calc(100% + 0.5rem);
  margin-top: 0;
}

.gm-recipient-settings ::v-deep .VueTables__table td:last-child .relative.float-right > .absolute.pin-r > .bg-white > div > div {
  display: flex;
  align-items: center;
  min-height: 2.75rem;
}

.gm-recipient-settings ::v-deep .VueTables__table td:last-child .relative.float-right > .absolute.pin-r > .bg-white > div:last-child > div {
  color: var(--gs-danger);
}

.gm-recipient-settings ::v-deep .VuePagination__pagination-item {
  min-width: 2.75rem;
  min-height: 2.75rem;
  padding: 0;
}

.gm-recipient-settings ::v-deep .VuePagination__pagination-item > a {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.75rem;
  min-height: 2.75rem;
}

@media (max-width: 40rem) {
  .gm-recipient-settings ::v-deep #gd-server-table-view .new-add-btn-wrapper {
    position: static;
    width: 100%;
    margin: 0 0 1rem;
  }

  .gm-recipient-settings ::v-deep #gd-server-table-view .new-add-btn-wrapper > .flex,
  .gm-recipient-settings ::v-deep #gd-server-table-view .new-add-btn-wrapper > .flex > .flex {
    flex-wrap: wrap;
    width: 100%;
    min-width: 0;
    gap: 0.75rem;
  }

  .gm-recipient-settings ::v-deep #gd-server-table-view .new-add-btn {
    width: 100%;
    min-height: 2.75rem;
    margin: 0;
  }

  .gm-recipient-settings ::v-deep #gd-server-table-view .VueTables__search {
    float: none !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .gm-recipient-settings ::v-deep #gd-server-table-view .VueTables__search-field {
    box-sizing: border-box;
    width: 100% !important;
    margin-top: 0 !important;
    margin-right: 0 !important;
    margin-bottom: 1rem !important;
    margin-left: 0 !important;
  }

  .gm-recipient-settings ::v-deep #gd-server-table-view .VueTables__search__input {
    box-sizing: border-box;
    width: 100% !important;
    max-width: 100%;
    min-height: 2.75rem;
    margin: 0 !important;
    padding-left: 2.75rem !important;
  }

  .gm-recipient-settings ::v-deep #gd-server-table-view .VueTables__search-field::before {
    top: 10px;
    left: 12px;
    pointer-events: none;
  }

  .gm-recipient-settings ::v-deep #gd-server-table-view .VueTables__limit {
    float: none;
    position: static;
    margin: 0 0 1rem;
  }

  .gm-recipient-settings ::v-deep #gd-server-table-view .VueTables__limit-field {
    position: relative;
    display: inline-flex;
    align-items: center;
    min-height: 2.75rem;
  }

  .gm-recipient-settings ::v-deep #gd-server-table-view .VueTables__limit-field::after {
    top: 50%;
    right: 0.375rem;
    transform: translateY(-50%);
  }

  .gm-recipient-settings ::v-deep #gd-server-table-view .VueTables__limit-field select {
    min-width: 5rem;
    min-height: 2.75rem;
    height: 2.75rem;
  }
}
</style>
